import NavShell from '../components/NavShell';

export default function PrivacyPage() {
  return (
    <NavShell>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <h1 className="text-3xl font-bold text-[#111827]">Privacy Policy</h1>
        <p className="text-sm text-gray-500">Last updated: April 11, 2026</p>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">1. What Data We Collect</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            We collect the following types of information when you use Swipe Opinion:
          </p>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li><strong>Account information:</strong> email address, name, and authentication credentials</li>
            <li><strong>Profile data:</strong> demographic information you provide during onboarding and profiler modules (e.g., age, gender, zip code, employment, household details)</li>
            <li><strong>Survey responses:</strong> answers to pre-qualification questions and survey completions</li>
            <li><strong>Device information:</strong> browser type, operating system, device type, IP address, and timezone</li>
            <li><strong>Usage data:</strong> login times, survey activity, points earned and redeemed, and platform interactions</li>
            <li><strong>Writing samples:</strong> text responses submitted for account verification purposes</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">2. How We Use Your Data</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li><strong>Survey matching:</strong> to match you with relevant surveys based on your demographic profile and interests</li>
            <li><strong>Platform improvement:</strong> to analyze usage patterns and improve the user experience</li>
            <li><strong>Communications:</strong> to send you notifications about new surveys, account activity, points expiration warnings, and platform updates</li>
            <li><strong>SMS notifications:</strong> if you provide a phone number and opt in, we use it to send survey alerts and account notifications via SMS</li>
            <li><strong>Fraud prevention:</strong> to detect and prevent fraudulent activity on the platform</li>
            <li><strong>Points and rewards:</strong> to manage your points balance and process redemptions</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">3. How We Share Your Data</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            We take your privacy seriously. Here is how your data may be shared:
          </p>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li><strong>Survey buyers:</strong> receive only aggregated and anonymized data. We never sell or share your personally identifiable information (PII) with survey buyers or research companies.</li>
            <li><strong>Survey providers:</strong> receive a limited, anonymized member identifier to route you to surveys. They do not receive your name, email, or other PII.</li>
            <li><strong>Payment processors:</strong> when you redeem points, we share necessary information (e.g., email for PayPal) with payment providers to fulfill your redemption.</li>
            <li><strong>Twilio:</strong> we use Twilio (via Supabase) to deliver SMS messages to members who have opted in to SMS notifications.</li>
            <li><strong>Resend:</strong> we use Resend to deliver transactional emails including survey invitations, redemption confirmations, and account notifications.</li>
            <li><strong>Legal requirements:</strong> we may disclose information when required by law, legal process, or government request.</li>
          </ul>
          <p className="text-sm text-[#111827]/80 leading-relaxed font-medium">
            We do not sell your personal information to third parties.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">4. Data Retention</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            We retain your account and profile data for as long as your account is active. If your
            account is inactive for more than 180 days, we may begin the data retention wind-down
            process. Writing samples submitted for verification are retained for 12 months and then
            automatically deleted. Survey response data is retained in anonymized form for analytical
            purposes. If you delete your account, we will remove your personal data within 30 days,
            except where retention is required by law.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">5. Your Rights</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            You have the following rights regarding your personal data:
          </p>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li><strong>Access:</strong> you can request a copy of the personal data we hold about you</li>
            <li><strong>Deletion:</strong> you can request that we delete your personal data and account</li>
            <li><strong>Correction:</strong> you can update your profile information at any time through the app</li>
            <li><strong>Opt-out:</strong> you can opt out of promotional communications at any time</li>
            <li><strong>Data portability:</strong> you can request your data in a machine-readable format</li>
          </ul>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:privacy@swipeopinion.com" className="text-[#5B4FE8] hover:underline">
              privacy@swipeopinion.com
            </a>.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">6. Cookies</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            We use cookies and similar technologies to maintain your session, remember your
            preferences, and track attribution for user acquisition purposes. Essential cookies
            are required for the platform to function. We use a tracking cookie (rh_cid) to
            attribute your registration to the marketing channel that referred you. You can manage
            cookie preferences through your browser settings, though disabling essential cookies
            may affect platform functionality.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">7. CCPA Notice for California Residents</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            If you are a California resident, you have additional rights under the California
            Consumer Privacy Act (CCPA):
          </p>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li><strong>Right to know:</strong> you may request details about the categories and specific pieces of personal information we have collected about you</li>
            <li><strong>Right to delete:</strong> you may request deletion of your personal information, subject to certain exceptions</li>
            <li><strong>Right to opt-out of sale:</strong> we do not sell your personal information. If this changes, we will provide a "Do Not Sell My Personal Information" link</li>
            <li><strong>Right to non-discrimination:</strong> we will not discriminate against you for exercising your CCPA rights</li>
          </ul>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            To submit a CCPA request, contact us at{' '}
            <a href="mailto:privacy@swipeopinion.com" className="text-[#5B4FE8] hover:underline">
              privacy@swipeopinion.com
            </a>{' '}
            with the subject line "CCPA Request."
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">8. SMS Communications</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            If you opt in to SMS notifications during registration or in your profile settings,
            we will send you survey alerts and account notifications via SMS. Message frequency
            varies based on your preferences. Message and data rates may apply. You can opt out
            at any time by replying STOP to any SMS message, or by updating your preferences in
            your profile settings. For help, reply HELP or contact{' '}
            <a href="mailto:support@swipeopinion.com" className="text-[#5B4FE8] hover:underline">
              support@swipeopinion.com
            </a>.
          </p>
        </section>

        <section className="space-y-3 pb-8">
          <h2 className="text-xl font-semibold text-[#111827]">9. Contact</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Swipe Opinion is operated by One Nice Photo, LLC. If you have questions or concerns
            about this Privacy Policy or our data practices, please contact us at{' '}
            <a href="mailto:privacy@swipeopinion.com" className="text-[#5B4FE8] hover:underline">
              privacy@swipeopinion.com
            </a>.
          </p>
        </section>
      </main>
    </NavShell>
  );
}
